/**
 * SignoffAnalyticsCard · v2.7.65+E109
 * ══════════════════════════════════════════════════════════════════
 * Compact "Signed off by tier" bar chart for the Admin Dashboard.
 * Reads /api/admin/chat/signoff-analytics?days=30 and renders one bar
 * per role tier (Level 1 → Level 5) with the count. Below the chart
 * is a compact list of the 5 most-recent sign-offs so ops can see
 * who resolved what without opening the chat panel.
 *
 * Auto-refreshes every 60 s.
 */
import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, RefreshCw, Loader2, Trophy } from "lucide-react";
import adminApi from "@/lib/adminApi";

const TIER_COLOR = {
  1: "bg-slate-400",
  2: "bg-sky-500",
  3: "bg-emerald-500",
  4: "bg-amber-500",
  5: "bg-rose-500",
};

const LEADERBOARD_MEDAL = ["🥇", "🥈", "🥉"];

export default function SignoffAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/chat/signoff-analytics?days=${days}`);
      setData(r.data);
    } catch {
      /* silent */
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const tiers = data?.by_tier || [];
  const maxCount = Math.max(1, ...tiers.map((t) => t.count));
  const total = data?.total ?? 0;

  return (
    <div
      data-testid="signoff-analytics-card"
      className="rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-4 mb-3"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-blue-500"/>
          <div>
            <div className="text-sm font-bold">Sign-offs by tier</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-zinc-500">
              Last {data?.days ?? days} days · {total} total
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-7 text-[11px] border border-slate-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-950 px-1.5"
            data-testid="signoff-analytics-range"
          >
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
            <option value={365}>1y</option>
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500 dark:text-zinc-500"
            data-testid="signoff-analytics-refresh"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3"/>}
          </button>
        </div>
      </div>

      {/* Bar chart */}
      <div className="space-y-1.5" data-testid="signoff-analytics-chart">
        {tiers.map((t) => {
          const pct = maxCount ? Math.round((t.count / maxCount) * 100) : 0;
          const color = TIER_COLOR[t.tier] || "bg-zinc-400";
          return (
            <div key={t.tier} className="flex items-center gap-2 text-xs" data-testid={`signoff-analytics-row-${t.tier}`}>
              <div className="w-20 text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 flex-shrink-0">
                L{t.tier} · {t.label}
              </div>
              <div className="flex-1 bg-slate-100 dark:bg-zinc-900 rounded h-4 overflow-hidden relative">
                <div
                  className={`h-full ${color} transition-all duration-500`}
                  style={{ width: `${pct}%` }}
                />
                {t.count > 0 && (
                  <span className="absolute inset-0 flex items-center px-2 text-[10px] font-bold text-white mix-blend-difference">
                    {t.count}
                  </span>
                )}
              </div>
              <div className="w-8 text-right tabular-nums text-[11px] font-bold text-slate-600 dark:text-zinc-400">
                {t.count}
              </div>
            </div>
          );
        })}
      </div>

      {/* v2.7.65+E110 · Weekly leaderboard chip */}
      {Array.isArray(data?.week_leaderboard) && data.week_leaderboard.length > 0 && (
        <div
          className="mt-3 pt-3 border-t border-slate-100 dark:border-zinc-900"
          data-testid="signoff-analytics-leaderboard"
        >
          <div className="flex items-center gap-1.5 mb-2">
            <Trophy className="h-3.5 w-3.5 text-amber-500"/>
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">
              Weekly leaderboard · Top closers (7d)
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.week_leaderboard.map((l, i) => (
              <div
                key={l.supervisor_name}
                data-testid={`signoff-leaderboard-${i + 1}`}
                className={`flex items-center gap-2 rounded-full px-3 py-1 border ${
                  i === 0
                    ? "bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-800"
                    : i === 1
                      ? "bg-slate-50 border-slate-300 dark:bg-zinc-800 dark:border-zinc-700"
                      : "bg-orange-50 border-orange-300 dark:bg-orange-900/20 dark:border-orange-800"
                }`}
              >
                <span className="text-base leading-none" aria-hidden>
                  {LEADERBOARD_MEDAL[i] || "🎖"}
                </span>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${TIER_COLOR[l.tier] || "bg-zinc-400"}`}/>
                <span className="font-bold text-xs text-slate-800 dark:text-zinc-200 truncate max-w-[10rem]">
                  {l.supervisor_name}
                </span>
                <span className="text-[11px] tabular-nums font-bold text-slate-500 dark:text-zinc-400">
                  {l.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent sign-off list */}
      {Array.isArray(data?.recent) && data.recent.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-zinc-900">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500 mb-1.5">
            Recent sign-offs
          </div>
          <div className="space-y-1" data-testid="signoff-analytics-recent">
            {data.recent.slice(0, 5).map((r) => (
              <div key={r.id} className="text-[11px] flex items-center gap-2" data-testid={`signoff-recent-${r.id}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${TIER_COLOR[r.tier] || "bg-zinc-400"}`}/>
                <span className="font-bold text-slate-700 dark:text-zinc-300 truncate max-w-[9rem]">
                  {r.supervisor_name || "?"}
                </span>
                <span className="text-slate-500 dark:text-zinc-500">·</span>
                <span className="text-slate-500 dark:text-zinc-500 uppercase tracking-widest text-[9px]">
                  {r.action_type || "close"} → {r.target_status || "resolved"}
                </span>
                <span className="ml-auto text-slate-400 dark:text-zinc-600 tabular-nums text-[10px]">
                  {(r.created_at || "").slice(5, 16).replace("T", " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {total === 0 && !loading && (
        <div className="mt-3 text-center text-[11px] italic text-slate-500 dark:text-zinc-500" data-testid="signoff-analytics-empty">
          No sign-offs yet in this window.
        </div>
      )}
    </div>
  );
}
